import { jwtDecode } from "jwt-decode"
import { Clerk } from "@clerk/clerk-js"

const clerk = new Clerk(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string)

const ACTIVE_STATUSES = ["active", "trialing", "past_due"]
const INACTIVE_STATUSES = [
   "incomplete",
   "incomplete_expired",
   "canceled",
   "unpaid",
   "paused",
]

// Cache for auth data
let authCache: {
   token?: string
   decodedToken?: EzbotAppJWTPayload
   sessionStatus?: "active" | "inactive"
   lastChecked?: number
} = {}

// Cache expiration time (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000

// Clear the auth cache
const clearAuthCache = () => {
   authCache = {}
}

// Check if cache is valid
const isCacheValid = () => {
   return (
      authCache.lastChecked &&
      Date.now() - authCache.lastChecked < CACHE_EXPIRATION &&
      authCache.token &&
      authCache.decodedToken
   )
}

// Load clerk and fetch token with caching
const getAuthData = async (): Promise<{
   token: string | null
   decodedToken: EzbotAppJWTPayload | null
   sessionActive: boolean
}> => {
   // Use cached data if valid
   if (isCacheValid()) {
      return {
         token: authCache.token!,
         decodedToken: authCache.decodedToken!,
         sessionActive: authCache.sessionStatus === "active",
      }
   }

   // Otherwise fetch fresh data
   await clerk.load()
   const sessionActive = !!clerk.session
   let token = null
   let decodedToken = null

   if (sessionActive) {
      token = await clerk.session!.getToken()
      if (token) {
         decodedToken = jwtDecode(token) as EzbotAppJWTPayload
      }
   }

   // Update cache
   authCache = {
      token: token || undefined,
      decodedToken: decodedToken || undefined,
      sessionStatus: sessionActive ? "active" : "inactive",
      lastChecked: Date.now(),
   }

   return {
      token,
      decodedToken,
      sessionActive,
   }
}

type UserPublicMetadata = {
   isEzbotEmployee?: boolean
   stripePriceId?: string
}

type OrgPublicMetaData = {
   subscriptionId: string
   subscriptionStatus: string
}

type UserOrgs = Record<string, string>

type EzbotAppJWTPayload = {
   // Default JWT values plus
   user_metadata: UserPublicMetadata
   user_orgs: UserOrgs
   user_email: string
   user_id: string
   user_full_name: string
   org_metadata: OrgPublicMetaData
   org_name: string
   org_id: string
   org_role: string
   org_slug: string
   org_permissions: string[]
}

const getOrgMemberships = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return {}
   }

   const orgMemberships = decodedToken.user_orgs
   if (!orgMemberships) {
      return {}
   }

   return orgMemberships
}

const hasActiveStripeSubscription = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return false
   }

   const opm = decodedToken.org_metadata
   if (!opm) {
      return false
   }

   return ACTIVE_STATUSES.includes(opm.subscriptionStatus)
}

const hasInactiveSubscription = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return false
   }

   const opm = decodedToken.org_metadata
   if (!opm) {
      return false
   }

   return INACTIVE_STATUSES.includes(opm.subscriptionStatus)
}

const hasActiveTrial = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return false
   }

   const opm = decodedToken.org_metadata
   if (!opm) {
      return false
   }

   return opm.subscriptionStatus === "trialing"
}

const hasActiveOrg = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return false
   }

   return !!decodedToken.org_id
}

const hasActiveSession = async () => {
   const { sessionActive } = await getAuthData()
   return sessionActive
}

const getOrgId = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return undefined
   }

   return decodedToken.org_id
}

type BasicOrg = {
   id: string
   name: string
   slug: string
   publicMetadata: OrgPublicMetaData
   role: string
   permissions: string[]
}

const getOrg = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return undefined
   }

   const org = {
      id: decodedToken.org_id,
      name: decodedToken.org_name,
      slug: decodedToken.org_slug,
      publicMetadata: decodedToken.org_metadata,
      role: decodedToken.org_role,
      permissions: decodedToken.org_permissions,
   } as BasicOrg

   return org
}

const getUser = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return undefined
   }

   const user = {
      id: decodedToken.user_id,
      email: decodedToken.user_email,
      fullName: decodedToken.user_full_name,
      publicMetadata: decodedToken.user_metadata,
      orgs: decodedToken.user_orgs,
   }

   return user
}

const setOrgId = async (orgId: string) => {
   await clerk.load()
   clerk.setActive({ organization: orgId })
   // Clear cache after changing org
   clearAuthCache()
}

const getNumberOfOrgs = async () => {
   const orgMemberships = await getOrgMemberships()
   if (!orgMemberships) {
      return 0
   }

   return Object.keys(orgMemberships).length
}

const getSubscriptionStatus = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return null
   }

   const opm = decodedToken.org_metadata
   if (!opm) {
      return null
   }

   return opm.subscriptionStatus
}

const hasActiveSubscription = async () => {
   const subscriptionStatus = await getSubscriptionStatus()
   return ACTIVE_STATUSES.includes(subscriptionStatus!)
}

const setOnlyOrgAsActive = async () => {
   const memberships = await getOrgMemberships()
   const numOrgs = await getNumberOfOrgs()
   if (numOrgs === 0) {
      throw new Error("No orgs found")
   }

   if (numOrgs === 1) {
      const membershipKeys = Object.keys(memberships)
      await setOrgId(memberships[membershipKeys[0]])
   }
}

const setFirstOrgAsActive = async () => {
   const memberships = await getOrgMemberships()
   const numOrgs = await getNumberOfOrgs()
   if (numOrgs === 0) {
      throw new Error("No orgs found")
   }

   const membershipKeys = Object.keys(memberships)
   await setOrgId(memberships[membershipKeys[0]])
}

const hasStripePriceId = async () => {
   const { decodedToken } = await getAuthData()
   if (!decodedToken) {
      return false
   }

   return !!decodedToken.user_metadata.stripePriceId
}

export {
   clerk,
   ACTIVE_STATUSES,
   INACTIVE_STATUSES,
   UserPublicMetadata,
   OrgPublicMetaData,
   EzbotAppJWTPayload,
   BasicOrg,
   hasActiveStripeSubscription,
   hasActiveOrg,
   hasActiveSession,
   setFirstOrgAsActive,
   hasActiveSubscription,
   getOrgMemberships,
   getOrgId,
   setOrgId,
   getOrg,
   getNumberOfOrgs,
   setOnlyOrgAsActive,
   hasInactiveSubscription,
   getUser,
   hasStripePriceId,
   getSubscriptionStatus,
   clearAuthCache,
   hasActiveTrial,
   getAuthData,
}
